/**
 * SearchBar component with debounced search
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { debounce } from "../lib/search";
import type { SearchBarProps } from "../types/index";
import CloseButton from "./CloseButton";

export default function SearchBar({
  onSearch,
  isLoading = false,
  placeholder = "Search plug-ins and skills...",
  externalQuery,
  clearToken,
}: SearchBarProps) {
  const [query, setQuery] = useState(externalQuery ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  // Tracks the last value we reported to the parent so we can distinguish a
  // genuine external reset ("Clear filters") from the parent echoing our own
  // value back on re-render.
  // null = "never reported to parent yet"; avoids colliding with "" as a reset target
  const lastSentToParent = useRef<string | null>(null);
  const debouncedSearch = useMemo(
    () => debounce((q: string) => {
      lastSentToParent.current = q;
      onSearchRef.current(q);
    }, 300),
    [],
  );
  // Mirror `query` in a ref so the externalQuery sync effect can read the latest
  // value without taking `query` as a dependency — depending on it would re-run
  // the sync on every keystroke and stomp characters typed after a debounce.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    // Only sync when the parent sends a genuinely new value — not when it
    // echoes back the last value we already reported (which would stomp any
    // characters the user typed after the debounce fired).
    // Also update lastSentToParent so a subsequent external reset to the same
    // value (after a clear cycle) is not permanently suppressed.
    if (
      externalQuery !== undefined &&
      externalQuery !== queryRef.current &&
      externalQuery !== lastSentToParent.current
    ) {
      // Cancel any pending debounce so a stale in-flight value isn't sent to
      // the parent after the reset (e.g. "Clear filters" fires while a 300ms
      // debounce for the old query is still scheduled).
      debouncedSearch.cancel();
      lastSentToParent.current = externalQuery;
      setQuery(externalQuery);
    }
  }, [externalQuery, debouncedSearch]);

  useEffect(() => {
    // clearToken covers the case where the parent resets filter state but
    // externalQuery is already "" (unchanged), so the externalQuery effect
    // above can't fire. Each increment cancels any pending debounce and
    // empties the input without sending an onSearch call to the parent
    // (the parent's Clear-filters handler already called applyFilters("")).
    // Guard against the mount run (token starts at 0 and is only bumped on clear).
    if (!clearToken) return;
    debouncedSearch.cancel();
    lastSentToParent.current = "";
    setQuery("");
  }, [clearToken, debouncedSearch]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setQuery(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      debouncedSearch.cancel();
      lastSentToParent.current = query;
      onSearchRef.current(query);
    }
  };

  const handleClear = () => {
    setQuery("");
    lastSentToParent.current = "";
    debouncedSearch.cancel();
    onSearchRef.current("");
    inputRef.current?.focus();
  };

  return (
    <div style={{ marginBottom: "2rem" }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          ref={inputRef}
          disabled={isLoading}
          style={{
            width: "100%",
            padding: "0.75rem",
            paddingRight: "2.5rem",
            fontSize: "1rem",
            border: "1px solid var(--color-border)",
            borderRadius: "8px",
            boxSizing: "border-box",
            opacity: isLoading ? 0.6 : 1,
            cursor: isLoading ? "wait" : "text",
          }}
        />
        {query && !isLoading && (
          <CloseButton onClick={handleClear} ariaLabel="Clear search" />
        )}
        {isLoading && (
          <span
            style={{
              position: "absolute",
              right: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--color-text-muted)",
              fontSize: "0.9rem",
            }}
          >
            Loading...
          </span>
        )}
      </div>
      {query && (
        <p style={{ margin: "0.5rem 0 0 0", color: "var(--color-text-secondary)", fontSize: "0.9rem" }}>
          Searching for "{query}"
        </p>
      )}
    </div>
  );
}
